/**
 * `Popover` — portal, flip + shift, rich content (dialog). The companion to
 * `Tooltip`: everything that needs a link, button, table or multi-paragraph
 * body goes here instead of in a tooltip (spec: labels-review/ux.md §12.3).
 *
 * - rendered to `document.body` with `position: fixed`.
 * - uses the shared `useAnchoredPosition` hook (flip + shift + visible-
 *   trigger clipping).
 * - `role="dialog"` with an `aria-label`; Escape closes and focus returns to
 *   the trigger. Outside click closes.
 * - z-index token `--z-popover: 900` (below modal/sheet).
 *
 * This is the primitive the TrustChip "Why?" popover now sits on top of so
 * the dashboard has one anchored-content stack instead of three.
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useC } from '../../lib/theme.js';
import { useAnchoredPosition, type AnchorSide } from './useAnchoredPosition.js';

export interface PopoverProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorRef: React.RefObject<HTMLElement>;
  readonly children: ReactNode;
  readonly side?: AnchorSide;
  readonly ariaLabel: string;
  readonly id?: string;
  readonly maxWidth?: number;
  readonly 'data-testid'?: string;
  /** Visual chrome: when true, no background/border is applied — the caller
   *  renders its own. Default false. */
  readonly bare?: boolean;
  readonly style?: CSSProperties;
}

export function Popover({
  open,
  onClose,
  anchorRef,
  children,
  side = 'bottom',
  ariaLabel,
  id,
  maxWidth = 360,
  'data-testid': testid,
  bare = false,
  style,
}: PopoverProps): JSX.Element | null {
  const C = useC();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    // Capture so the trigger's own Enter/Space handler cannot swallow Escape.
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, anchorRef, onClose]);

  const pos = useAnchoredPosition(anchorRef, panelRef, { open, side, offset: 6 });

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label={ariaLabel}
      data-testid={testid}
      data-placement={pos.side}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        visibility: pos.visible ? 'visible' : 'hidden',
        zIndex: 'var(--z-popover, 900)' as unknown as number,
        maxWidth: `min(${maxWidth}px, calc(100vw - 16px))`,
        maxHeight: 'min(70vh, 520px)',
        overflowY: 'auto',
        ...(bare ? {} : {
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: 14,
          boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        }),
        ...(style ?? {}),
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
